<template>
    <VcSelect
        v-model="model"
        class="sound-select"
        :placeholder="placeholder"
        :options="options"
        :config="{
            valueKey: 'RecordID',
            labelKey: 'Description'
        }"
        :filterable="filterable"
        :class="selectClass"
        @update:visible="onDropdownVisibleChange"
    >
        <template #option="{ option }">
            <div
                v-if="option.Rec_Path"
                class="flex items-center mx-2 flex-grow"
            >
                <span
                    class="sound-label"
                    :title="option.Description"
                >
                    {{ option.Description }}
                </span>
                <div
                    v-if="isDropdownOpen"
                    class="sound-player-wrapper"
                    @click.stop
                    @dblclick.stop
                >
                    <VcSoundPlayer
                        :ref="(el: ComponentPublicInstance | null) => setSoundPlayerRef(option.RecordID, el)"
                        class="sound-selector"
                        :sound-id="option.RecordID"
                        :src="option.Rec_Path"
                        :simple="false"
                    />
                </div>
            </div>
            <div v-else>
                {{ option.Description }}
            </div>
        </template>
    </VcSelect>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import type { ComponentPublicInstance } from 'vue'
import { VcSelect, VcSoundPlayer } from '@voicenter-team/voicenter-ui-plus'
import { UserRecord } from '@/types/entities'
import { getSoundObject, NONE_SOUND } from '@/composables/globalData/useEntitiesData'

/* Props */
const props = withDefaults(
    defineProps<{
        modelValue?: string | number
        placeholder?: string
        filterable?: boolean
        selectClass?: string
        options?: Array<UserRecord>
    }>(),
    {
        modelValue: undefined,
        placeholder: '',
        filterable: true,
        selectClass: '',
        options: () => []
    }
)

/* Emit */
const emit = defineEmits<{
    (event: 'update:modelValue', value: string | number | undefined): void
}>()

/* Data */
const soundPlayerRefs = ref<Record<number, ComponentPublicInstance | null>>({})
const isDropdownOpen = ref(false)

/* Computed */
const model = computed({
    get () {
        if (!props.modelValue || props.modelValue === '' || props.modelValue === 'none') {
            return NONE_SOUND.RecordID
        }
        return props.modelValue ? getSoundObject(props.options, props.modelValue, NONE_SOUND).RecordID : undefined
    },
    set (value) {
        emit('update:modelValue', value)
    }
})

/* Methods */
function setSoundPlayerRef (recordId: number, el: ComponentPublicInstance | null) {
    soundPlayerRefs.value[recordId] = el
}

function onDropdownVisibleChange (visible: boolean) {
    if (!visible) {
        stopAllPlayers()
    }
    isDropdownOpen.value = visible
}

function stopAllPlayers () {
    props.options.forEach(option => {
        const refEl = soundPlayerRefs.value[option.RecordID] as (ComponentPublicInstance & { stop?: () => void }) | null
        if (refEl?.stop) {
            refEl.stop()
        }
    })
}
</script>

<style scoped>
.sound-label {
    @apply truncate;
    flex-grow: 1;
    max-width: 350px;
}

.sound-selector {
    @apply ml-3;
    min-width: 270px;
    width: auto;
}

[dir='rtl'] {
    .sound-selector {
        @apply mr-3;
        margin-left: unset;
    }
}
</style>
